import { useState, useRef, useEffect, useCallback } from "react";
import type { PointerEvent, TouchEvent } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Mic, Send } from "lucide-react";
import BottomNav from "@/components/feature/BottomNav";
import { assistantApi } from "@/api/modules/assistant";
import type { AssistantCoachRecommendation, AssistantMessage, AssistantServiceRecommendation } from "@/api/modules/assistant";
import { commentApi } from "@/api/modules/comment";
import { orderApi } from "@/api/modules/order";
import type { OrderListItem } from "@/api/modules/order";
import { wellnessApi } from "@/api/modules/wellness";
import type {
  WellnessContentCard,
  WellnessGoodsCard,
  WellnessGuideResult,
  WellnessNextCare,
  WellnessPersonaCard,
} from "@/api/modules/wellness";
import { buildLoginRedirect } from "@/lib/navigation";
import { auth } from "@/lib/auth";
import { RequestError } from "@/lib/request";
import { locationStore } from "@/lib/location";
import { storage, storageKeys } from "@/lib/storage";
import { resolveCustomerAssetUrl } from "@/lib/asset";
import { preloadWechatVoice, startWechatVoiceRecord, stopWechatVoiceAndTranslate } from "@/lib/wechat-voice";

/* ─── Types ─── */
interface Message {
  id: number;
  role: "user" | "ai";
  content: string;
  time: string;
  services?: ServiceCard[];
  coaches?: CoachCard[];
}

interface ServiceCard {
  goodsId?: number;
  name: string;
  desc: string;
  price: string;
  duration: string;
}

interface CoachCard {
  coachId?: number;
  name: string;
  photo: string;
  rating: string;
  orderNum: number;
  desc: string;
}

interface TipCard {
  id: number;
  tag: string;
  tagColor: string;
  tagBg: string;
  title: string;
  body: string;
  icon: string;
  iconColor: string;
  iconBg: string;
}

interface SceneCard {
  id: number;
  icon: string;
  label: string;
  desc: string;
  color: string;
  bg: string;
  services: ServiceCard[];
}

interface SeasonalTip {
  icon: string;
  title: string;
  desc: string;
  color: string;
  bg: string;
  detailTitle: string;
  detailBody: string;
  sourceName: string;
  sourceUrl: string;
}

interface TodayTip {
  headerTag: string;
  imageUrl: string;
  tag: string;
  title: string;
  body: string;
}

interface NextCareCard {
  title: string;
  mainText: string;
  subText: string;
  buttonText: string;
  targetUrl: string;
}

interface ServiceRecord {
  id: number;
  date: string;
  service: string;
  therapist: string;
  score: number;
  feedback: string;
  durationMinutes: number;
  reviewed: boolean;
}

/* ─── Static Data ─── */
const TIPS = [
  {
    id: 1,
    tag: "谷雨节气",
    tagColor: "#17A87B",
    tagBg: "#EDFBF4",
    title: "谷雨祛湿，疏肝理气",
    body: "谷雨时节湿气渐重，宜多做经络疏通，饮食以清淡为主，避免生冷油腻，可常饮薏米红豆茶健脾祛湿。",
    icon: "ri-leaf-line",
    iconColor: "#17A87B",
    iconBg: "#EDFBF4",
  },
  {
    id: 2,
    tag: "每日穴位",
    tagColor: "#E8860A",
    tagBg: "#FFF8E7",
    title: "按压合谷穴，缓解头肩疲劳",
    body: "合谷穴位于虎口处，每天按压3-5分钟，可有效缓解头痛、肩颈紧张，是上班族居家保健的必备穴位。",
    icon: "ri-hand-heart-line",
    iconColor: "#E8860A",
    iconBg: "#FFF8E7",
  },
  {
    id: 3,
    tag: "睡眠养生",
    tagColor: "#9B72CF",
    tagBg: "#F3EEFF",
    title: "亥时入眠，养肾最佳",
    body: "中医认为亥时（21-23点）是三焦经当令，此时入睡有助于全身气血通畅，对养肾护阳、改善睡眠质量大有裨益。",
    icon: "ri-moon-line",
    iconColor: "#9B72CF",
    iconBg: "#F3EEFF",
  },
  {
    id: 4,
    tag: "春季养生",
    tagColor: "#FF6018",
    tagBg: "#FFF4EC",
    title: "春季护肝，情志为先",
    body: "春季肝气升发，情绪波动大。保持心情舒畅，避免过度劳累，配合香薰按摩有助疏肝解郁、调节情绪。",
    icon: "ri-sun-line",
    iconColor: "#FF6018",
    iconBg: "#FFF4EC",
  },
];

const SCENE_CARDS = [
  {
    id: 1,
    icon: "ri-computer-line",
    label: "久坐族",
    desc: "腰背肩颈",
    color: "#FF6018",
    bg: "#FFF4EC",
    services: [
      { name: "颈肩放松", desc: "针对久坐引发的肩颈紧张，快速舒缓", price: "¥138起", duration: "45分钟" },
      { name: "中医推拿", desc: "疏通经络，缓解腰背酸胀", price: "¥188起", duration: "60分钟" },
    ],
  },
  {
    id: 2,
    icon: "ri-moon-line",
    label: "睡眠差",
    desc: "调节放松",
    color: "#9B72CF",
    bg: "#F3EEFF",
    services: [
      { name: "足部理疗", desc: "刺激足底反射区，改善睡眠质量", price: "¥128起", duration: "60分钟" },
      { name: "精油SPA", desc: "薰衣草配方，镇静安神效果显著", price: "¥398起", duration: "120分钟" },
    ],
  },
  {
    id: 3,
    icon: "ri-women-line",
    label: "孕期妈妈",
    desc: "专业呵护",
    color: "#E8860A",
    bg: "#FFF8E7",
    services: [
      { name: "孕期舒缓", desc: "专业孕期手法，安全舒缓腰背不适", price: "¥268起", duration: "60分钟" },
    ],
  },
  {
    id: 4,
    icon: "ri-run-line",
    label: "运动恢复",
    desc: "肌肉修复",
    color: "#17A87B",
    bg: "#EDFBF4",
    services: [
      { name: "全身放松", desc: "深层肌肉放松，加速运动后恢复", price: "¥298起", duration: "90分钟" },
    ],
  },
];

const SEASONAL_TIPS = [
  {
    icon: "ri-leaf-line",
    title: "谷雨祛湿",
    desc: "经络疏通健脾祛湿",
    color: "#17A87B",
    bg: "#EDFBF4",
    detailTitle: "谷雨时节，先把祛湿和轻活动放进日常",
    detailBody: "谷雨前后湿气渐重，日常更适合清淡饮食、少量多次补水，并通过轻柔活动和放松服务缓解久坐后的紧绷感。若有明显疼痛、发热或麻木无力，应优先做专业评估。",
    sourceName: "健康素养提示",
    sourceUrl: "",
  },
  {
    icon: "ri-sun-line",
    title: "春季护肝",
    desc: "精油按摩疏肝解郁",
    color: "#FF6018",
    bg: "#FFF4EC",
    detailTitle: "春季作息稳定，情绪和身体都更容易放松",
    detailBody: "春季适合把步行、拉伸等轻活动重新放回日程，也可以选择温和的精油或舒缓类服务，让肩颈和情绪慢慢松下来。活动和服务强度以舒适为主，不追求疼痛感。",
    sourceName: "WHO Physical activity",
    sourceUrl: "",
  },
  {
    icon: "ri-drop-line",
    title: "润燥排毒",
    desc: "足部理疗促进代谢",
    color: "#9B72CF",
    bg: "#F3EEFF",
    detailTitle: "干燥时节，把补水、作息和温和放松做扎实",
    detailBody: "空气偏干时，可以把补水、皮肤保湿、稳定睡眠和适量活动放进日常习惯。足部或全身放松更适合作为舒缓体验，不替代医疗诊疗，也不承诺治疗效果。",
    sourceName: "中国居民膳食指南",
    sourceUrl: "",
  },
];

const DEFAULT_TODAY_IMAGE =
  "https://readdy.ai/api/search-image?query=traditional%20chinese%20medicine%20herbs%20bamboo%20leaves%20green%20natural%20wellness%20spa%20calm%20serene%20background%20minimal%20aesthetic&width=800&height=280&seq=tip_hero_v1&orientation=landscape";

const TIP_PALETTE = [
  { tagColor: "#17A87B", tagBg: "#EDFBF4", icon: "ri-leaf-line", iconColor: "#17A87B", iconBg: "#EDFBF4" },
  { tagColor: "#E8860A", tagBg: "#FFF8E7", icon: "ri-hand-heart-line", iconColor: "#E8860A", iconBg: "#FFF8E7" },
  { tagColor: "#9B72CF", tagBg: "#F3EEFF", icon: "ri-moon-line", iconColor: "#9B72CF", iconBg: "#F3EEFF" },
  { tagColor: "#FF6018", tagBg: "#FFF4EC", icon: "ri-sun-line", iconColor: "#FF6018", iconBg: "#FFF4EC" },
];

const SCENE_PALETTE = [
  { icon: "ri-computer-line", color: "#FF6018", bg: "#FFF4EC" },
  { icon: "ri-moon-line", color: "#9B72CF", bg: "#F3EEFF" },
  { icon: "ri-run-line", color: "#17A87B", bg: "#EDFBF4" },
  { icon: "ri-hand-heart-line", color: "#E8860A", bg: "#FFF8E7" },
];

const splitTags = (value?: string) =>
  String(value || "")
    .split(/[,，、\s]+/)
    .map((item) => item.trim())
    .filter(Boolean);

const firstText = (...values: Array<string | undefined | null>) =>
  values.find((value) => typeof value === "string" && value.trim().length > 0)?.trim() || "";

const limitText = (value: string, maxLength: number) => {
  const text = String(value || "").trim();
  return text.length > maxLength ? text.slice(0, maxLength) : text;
};

const splitParagraphs = (value: string) =>
  String(value || "")
    .split(/\r?\n+/)
    .map((item) => item.trim())
    .filter(Boolean);

const toNumber = (value: unknown, fallback = 0) => {
  const numberValue = Number(value);
  return Number.isFinite(numberValue) ? numberValue : fallback;
};

const formatPrice = (price?: number) => {
  if (price === undefined || price === null || Number.isNaN(Number(price))) {
    return "";
  }
  return `¥${Number(price).toFixed(0)}起`;
};

const formatDuration = (duration?: number) => (duration ? `${duration}分钟` : "");

const formatRecordDate = (value?: string) => {
  if (!value) {
    return "-";
  }
  const match = value.match(/(\d{4})-(\d{2})-(\d{2})/);
  if (match) {
    return `${match[2]}-${match[3]}`;
  }
  return value.slice(0, 5);
};

const sumOrderDuration = (order: OrderListItem) => {
  const goodsDuration = (order.order_goods || []).reduce((sum, item) => {
    return sum + toNumber(item.duration) * Math.max(1, toNumber(item.goods_num, 1));
  }, 0);
  const appendDuration =
    toNumber(order.total_append_duration) ||
    (order.order_append || []).reduce((sum, item) => {
      return sum + (toNumber(item.total_duration) || toNumber(item.duration) * Math.max(1, toNumber(item.goods_num, 1)));
    }, 0);
  return goodsDuration + appendDuration;
};

const primaryOrderGoodsName = (order: OrderListItem) =>
  firstText(order.order_goods?.[0]?.goods_name, "服务项目");

const buildRecordFeedback = (order: OrderListItem, comment?: { service_comment?: number; comment?: string }) => {
  if (comment?.comment) {
    return comment.comment;
  }
  if (order.comment_btn === 1) {
    return "待评价";
  }
  if (order.look_comment_btn === 1) {
    return "已评价";
  }
  return firstText(order.order_status_desc, "已完成");
};

const mapOrderToServiceRecord = (
  order: OrderListItem,
  comment?: { service_comment?: number; comment?: string },
): ServiceRecord => ({
  id: order.id,
  date: formatRecordDate(firstText(order.server_finish_time, order.appoint_date, order.create_time)),
  service: primaryOrderGoodsName(order),
  therapist: firstText(order.coach_info?.name, "待分配技师"),
  score: Math.max(0, Math.min(5, toNumber(comment?.service_comment))),
  feedback: buildRecordFeedback(order, comment),
  durationMinutes: sumOrderDuration(order),
  reviewed: Boolean(comment?.service_comment || comment?.comment || order.look_comment_btn === 1),
});

const buildNextCareCard = (source?: WellnessNextCare): NextCareCard => ({
  title: firstText(source?.title, "下次调理建议"),
  mainText: firstText(source?.main_text, "3天后 · 4月18日"),
  subText: firstText(source?.sub_text, "距上次全身放松已14天"),
  buttonText: firstText(source?.button_text, "立即预约"),
  targetUrl: firstText(source?.target_url, "/booking"),
});

const buildTodayTip = (items?: WellnessContentCard[]): TodayTip => {
  const source = items?.[0];
  const tags = splitTags(source?.tags);
  return {
    headerTag: firstText(source?.season, source?.subtitle, tags[0], "谷雨节气"),
    imageUrl: resolveCustomerAssetUrl(firstText(source?.image_url, DEFAULT_TODAY_IMAGE)),
    tag: firstText(tags[0], source?.subtitle, source?.season, "谷雨节气"),
    title: firstText(source?.title, "谷雨祛湿，疏肝理气"),
    body: firstText(
      source?.summary,
      source?.content,
      "谷雨时节湿气渐重，宜多做经络疏通，饮食以清淡为主，避免生冷油腻。可常饮薏米红豆茶健脾祛湿，配合按摩效果更佳。",
    ),
  };
};

const buildTipCards = (items?: WellnessContentCard[]): TipCard[] => {
  if (!items?.length) {
    return TIPS;
  }
  return items.slice(0, 5).map((item, index) => {
    const fallback = TIPS[index % TIPS.length];
    const palette = TIP_PALETTE[index % TIP_PALETTE.length];
    const tag = firstText(splitTags(item.tags)[0], item.subtitle, item.season, fallback.tag);
    return {
      id: Number(item.id || index + 1),
      tag,
      tagColor: palette.tagColor,
      tagBg: palette.tagBg,
      title: firstText(item.title, fallback.title),
      body: firstText(item.summary, item.content, fallback.body),
      icon: firstText(item.icon, palette.icon),
      iconColor: palette.iconColor,
      iconBg: palette.iconBg,
    };
  });
};

const buildSceneCards = (items?: WellnessPersonaCard[]): SceneCard[] => {
  if (!items?.length) {
    return SCENE_CARDS;
  }
  return items.slice(0, 8).map((item, index) => {
    const fallback = SCENE_CARDS[index % SCENE_CARDS.length];
    const palette = SCENE_PALETTE[index % SCENE_PALETTE.length];
    return {
      id: Number(item.id || index + 1),
      icon: firstText(item.icon, palette.icon),
      label: firstText(item.name, fallback.label),
      desc: limitText(firstText(item.summary, item.description, fallback.desc), 6),
      color: palette.color,
      bg: palette.bg,
      services: buildSceneServices(item.goods, fallback.services),
    };
  });
};

const buildSceneServices = (items: WellnessGoodsCard[] | undefined, fallback: ServiceCard[]) => {
  if (!items?.length) {
    return fallback;
  }
  return items.slice(0, 3).map((item) => ({
    goodsId: item.goods_id,
    name: firstText(item.name, "服务项目"),
    desc: firstText(item.recommend_reason, "适合当前状态的调理项目"),
    price: firstText(formatPrice(item.price), "¥0起"),
    duration: firstText(formatDuration(item.duration), "60分钟"),
  }));
};

const buildSeasonalTips = (items?: WellnessContentCard[]): SeasonalTip[] => {
  if (!items?.length) {
    return SEASONAL_TIPS;
  }
  return items.slice(0, 3).map((item, index) => {
    const fallback = SEASONAL_TIPS[index % SEASONAL_TIPS.length];
    const palette = TIP_PALETTE[index % TIP_PALETTE.length];
    return {
      icon: firstText(item.icon, fallback.icon),
      title: limitText(firstText(item.title, fallback.title), 4),
      desc: limitText(firstText(item.summary, item.subtitle, fallback.desc), 8),
      color: palette.iconColor,
      bg: palette.iconBg,
      detailTitle: firstText(item.title, fallback.detailTitle),
      detailBody: firstText(item.content, item.summary, fallback.detailBody),
      sourceName: firstText(item.source_name, fallback.sourceName),
      sourceUrl: firstText(item.source_url, fallback.sourceUrl),
    };
  });
};

/* ─── Chat Data Mapping ─── */
const ASSISTANT_SCENE = "ASSISTANT";
const ASSISTANT_CHAT_CACHE_KEY = "athome_customer_assistant_chat_cache";
const MAX_CACHED_CHAT_MESSAGES = 80;

const DEFAULT_CHAT_MESSAGE: Message = {
  id: 0,
  role: "ai",
  content: "你好，我是妙柔健康。你可以描述不适部位、持续时间和诱因，我会先帮你做日常护理和风险提醒。",
  time: "10:30",
};

interface AssistantChatCache {
  token: string;
  savedAt: number;
  conversationId?: number;
  conversationNo?: string;
  messages: Message[];
}

const safeReadAssistantChatCache = (): AssistantChatCache | null => {
  if (typeof window === "undefined") {
    return null;
  }
  const token = auth.getToken();
  if (!token) {
    return null;
  }
  const cached = storage.get<AssistantChatCache>(ASSISTANT_CHAT_CACHE_KEY);
  if (!cached || cached.token !== token || !Array.isArray(cached.messages) || cached.messages.length === 0) {
    storage.remove(ASSISTANT_CHAT_CACHE_KEY);
    return null;
  }
  return cached;
};

const normalizeCachedMessages = (messages?: Message[]) =>
  messages?.length ? messages : [DEFAULT_CHAT_MESSAGE];

const formatChatTime = (value?: string) => {
  const date = value ? new Date(value.replace(" ", "T")) : new Date();
  if (Number.isNaN(date.getTime())) {
    return "--:--";
  }
  return `${date.getHours()}:${String(date.getMinutes()).padStart(2, "0")}`;
};

const mapChatMessage = (item: AssistantMessage): Message => ({
  id: Number(item.message_id || Date.now()),
  role: item.role === "user" ? "user" : "ai",
  content: firstText(item.content, item.content_digest, ""),
  time: formatChatTime(item.create_time),
});

const mapRecommendedServices = (items?: AssistantServiceRecommendation[]): ServiceCard[] =>
  (items || [])
    .filter((item) => firstText(item.name))
    .map((item) => ({
      goodsId: item.goods_id,
      name: firstText(item.name, "服务项目"),
      desc: firstText(item.match_reason, item.category_name, "适合当前咨询内容的服务项目"),
      price: firstText(formatPrice(item.price), "价格以详情页为准"),
      duration: firstText(formatDuration(item.duration), "时长以详情页为准"),
    }));

const mapRecommendedCoaches = (items?: AssistantCoachRecommendation[]): CoachCard[] =>
  (items || [])
    .filter((item) => firstText(item.name))
    .map((item) => ({
      coachId: item.coach_id,
      name: firstText(item.name, "优质技师"),
      photo: resolveCustomerAssetUrl(item.work_photo || ""),
      rating: item.rating ? String(item.rating) : "暂无评分",
      orderNum: Number(item.order_num || 0),
      desc: firstText(item.recommend_reason, item.city_name ? `${item.city_name}可服务技师` : "当前可服务技师"),
    }));

const buildAssistantErrorMessage = (message: string): Message => ({
  id: Date.now() + 1,
  role: "ai",
  content: message,
  time: formatChatTime(),
});

/* ─── Page ─── */
export default function AssistantPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [initialChatCache] = useState(() => safeReadAssistantChatCache());
  const [activeTab, setActiveTab] = useState<"home" | "chat" | "records">("home");
  const [messages, setMessages] = useState<Message[]>(() => normalizeCachedMessages(initialChatCache?.messages));
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [conversationId, setConversationId] = useState<number | undefined>(initialChatCache?.conversationId);
  const [conversationNo, setConversationNo] = useState<string | undefined>(initialChatCache?.conversationNo);
  const [chatHydrated, setChatHydrated] = useState(Boolean(initialChatCache));
  const [expandedScene, setExpandedScene] = useState<number | null>(null);
  const [isRecording, setIsRecording] = useState(false);
  const [voiceError, setVoiceError] = useState<string | null>(null);
  const [wellnessGuide, setWellnessGuide] = useState<WellnessGuideResult | null>(null);
  const [selectedSeasonalTip, setSelectedSeasonalTip] = useState<SeasonalTip | null>(null);
  const [serviceRecords, setServiceRecords] = useState<ServiceRecord[]>([]);
  const [recordsLoading, setRecordsLoading] = useState(false);
  const [recordsError, setRecordsError] = useState("");
  const [recordsHydrated, setRecordsHydrated] = useState(false);
  const recognitionRef = useRef<SpeechRecognition | null>(null);
  const wechatRecordingRef = useRef(false);
  const voicePressingRef = useRef(false);
  const voiceStartingRef = useRef(false);
  const voiceStoppingRef = useRef(false);
  const chatEndRef = useRef<HTMLDivElement>(null);
  const nextCare = buildNextCareCard(wellnessGuide?.next_care);
  const todayTip = buildTodayTip(wellnessGuide?.today);
  const wellnessTips = buildTipCards(wellnessGuide?.tips);
  const sceneCards = buildSceneCards(wellnessGuide?.personas);
  const seasonalTips = buildSeasonalTips(wellnessGuide?.seasonal);
  const selectedSeasonalParagraphs = selectedSeasonalTip ? splitParagraphs(selectedSeasonalTip.detailBody) : [];
  const completedRecordCount = serviceRecords.length;
  const reviewedRecordCount = serviceRecords.filter((item) => item.reviewed).length;
  const serviceDurationHours = serviceRecords.reduce((sum, item) => sum + item.durationMinutes, 0) / 60;
  const serviceRecordStats = [
    { icon: "ri-calendar-check-line", label: "累计服务", value: `${completedRecordCount}次`, color: "#FF6018", bg: "#FFF4EC" },
    { icon: "ri-star-line", label: "已评价", value: `${reviewedRecordCount}次`, color: "#E8860A", bg: "#FFF8E7" },
    { icon: "ri-timer-line", label: "服务时长", value: `${serviceDurationHours.toFixed(1)}h`, color: "#17A87B", bg: "#EDFBF4" },
  ];

  useEffect(() => {
    let cancelled = false;
    const loadWellnessGuide = async () => {
      try {
        const result = await wellnessApi.getGuide();
        if (!cancelled) {
          setWellnessGuide(result);
        }
      } catch {
        if (!cancelled) {
          setWellnessGuide(null);
        }
      }
    };
    void loadWellnessGuide();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (activeTab !== "records" || recordsHydrated) {
      return;
    }
    if (!auth.getToken()) {
      navigate(buildLoginRedirect(`${location.pathname}${location.search}`), { replace: true });
      return;
    }

    let cancelled = false;
    const loadServiceRecords = async () => {
      setRecordsLoading(true);
      setRecordsError("");
      try {
        const result = await orderApi.getOrderList({ page_no: 1, page_size: 50 });
        const completedOrders = (result.lists || [])
          .filter((order) => toNumber(order.order_status) === 6)
          .slice(0, 20);
        const commentResults = await Promise.allSettled(
          completedOrders.map((order) => {
            const commentId = toNumber(order.order_goods?.[0]?.comment_id);
            return commentId > 0 ? commentApi.getCommentDetail(commentId) : Promise.resolve(undefined);
          }),
        );
        if (cancelled) {
          return;
        }
        const mapped = completedOrders.map((order, index) => {
          const commentResult = commentResults[index];
          const comment = commentResult?.status === "fulfilled" ? commentResult.value : undefined;
          return mapOrderToServiceRecord(order, comment);
        });
        setServiceRecords(mapped);
        setRecordsHydrated(true);
      } catch (error) {
        if (cancelled) {
          return;
        }
        if (error instanceof RequestError && error.code === 1001) {
          navigate(buildLoginRedirect(`${location.pathname}${location.search}`), { replace: true });
          return;
        }
        setRecordsError(error instanceof Error ? error.message : "服务记录加载失败，请稍后重试。");
        setRecordsHydrated(true);
      } finally {
        if (!cancelled) {
          setRecordsLoading(false);
        }
      }
    };
    void loadServiceRecords();
    return () => {
      cancelled = true;
    };
  }, [activeTab, location.pathname, location.search, navigate, recordsHydrated]);

  const openNextCare = () => {
    navigate(nextCare.targetUrl || "/booking");
  };

  const openWellnessService = (service: ServiceCard) => {
    if (service.goodsId) {
      navigate(`/booking?goodsId=${service.goodsId}`);
      return;
    }
    navigate("/booking");
  };

  const openCoachDetail = (coach: CoachCard) => {
    if (coach.coachId) {
      navigate(`/therapist-detail?id=${coach.coachId}`);
    }
  };

  const showVoiceError = useCallback((message: string) => {
    setVoiceError(message);
    window.setTimeout(() => setVoiceError(null), 2500);
  }, []);

  const startRecording = useCallback(async () => {
    if (voiceStartingRef.current || recognitionRef.current || wechatRecordingRef.current) {
      return;
    }
    voiceStartingRef.current = true;
    try {
      try {
        const wechatStarted = await startWechatVoiceRecord();
        if (wechatStarted) {
          wechatRecordingRef.current = true;
          setVoiceError(null);
          setIsRecording(true);
          if (!voicePressingRef.current) {
            wechatRecordingRef.current = false;
            voiceStoppingRef.current = true;
            void stopWechatVoiceAndTranslate()
              .then((transcript) => {
                if (transcript) {
                  setInput((prev) => `${prev}${prev.trim() ? " " : ""}${transcript}`.trimStart());
                }
              })
              .catch((error) => showVoiceError(error instanceof Error ? error.message : "微信语音转文字失败，请再试一次或改用文字输入"))
              .finally(() => {
                voiceStoppingRef.current = false;
                setIsRecording(false);
              });
          }
          return;
        }
      } catch (error) {
        showVoiceError(error instanceof Error ? error.message : "微信录音初始化失败，请退出重进后再试");
        return;
      }
      const SpeechRecognitionAPI =
        (window as unknown as { SpeechRecognition?: typeof SpeechRecognition; webkitSpeechRecognition?: typeof SpeechRecognition }).SpeechRecognition ||
        (window as unknown as { SpeechRecognition?: typeof SpeechRecognition; webkitSpeechRecognition?: typeof SpeechRecognition }).webkitSpeechRecognition;
      if (!SpeechRecognitionAPI) {
        showVoiceError("当前浏览器不支持语音识别，请先使用文字输入");
        return;
      }
      const recognition = new SpeechRecognitionAPI();
      recognition.lang = "zh-CN";
      recognition.interimResults = false;
      recognition.maxAlternatives = 1;
      recognition.onresult = (e: SpeechRecognitionEvent) => {
        const transcript = e.results[0][0].transcript;
        setInput((prev) => `${prev}${prev.trim() ? " " : ""}${transcript}`.trimStart());
      };
      recognition.onerror = (event) => {
        setIsRecording(false);
        const errorCode = String((event as unknown as { error?: string }).error || "");
        if (errorCode === "not-allowed" || errorCode === "service-not-allowed") {
          showVoiceError("麦克风权限被拒绝，请在浏览器设置中允许麦克风");
          return;
        }
        if (errorCode === "no-speech") {
          showVoiceError("没有听清，可以再点一次麦克风重试");
          return;
        }
        showVoiceError("语音识别失败，请再试一次或改用文字输入");
      };
      recognition.onend = () => {
        setIsRecording(false);
        recognitionRef.current = null;
      };
      recognitionRef.current = recognition;
      try {
        recognition.start();
        setVoiceError(null);
        setIsRecording(true);
        if (!voicePressingRef.current) {
          recognition.stop();
        }
      } catch {
        recognitionRef.current = null;
        setIsRecording(false);
        showVoiceError("语音识别启动失败，请再试一次或改用文字输入");
      }
    } finally {
      voiceStartingRef.current = false;
    }
  }, [showVoiceError]);

  const stopRecording = useCallback(async () => {
    if (voiceStoppingRef.current) {
      return;
    }
    if (wechatRecordingRef.current) {
      setIsRecording(false);
      wechatRecordingRef.current = false;
      voiceStoppingRef.current = true;
      try {
        const transcript = await stopWechatVoiceAndTranslate();
        if (transcript) {
          setInput((prev) => `${prev}${prev.trim() ? " " : ""}${transcript}`.trimStart());
        } else {
          showVoiceError("没有识别到语音内容，可以再按住麦克风重试");
        }
      } catch (error) {
        showVoiceError(error instanceof Error ? error.message : "微信语音转文字失败，请再试一次或改用文字输入");
      } finally {
        voiceStoppingRef.current = false;
      }
      return;
    }
    const recognition = recognitionRef.current;
    if (recognition) {
      recognition.stop();
    }
    setIsRecording(false);
  }, [showVoiceError]);

  const handleVoicePointerDown = useCallback((event: PointerEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.currentTarget.setPointerCapture(event.pointerId);
    if (voicePressingRef.current) {
      return;
    }
    voicePressingRef.current = true;
    void startRecording();
  }, [startRecording]);

  const handleVoicePointerUp = useCallback((event: PointerEvent<HTMLButtonElement>) => {
    event.preventDefault();
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    voicePressingRef.current = false;
    void stopRecording();
  }, [stopRecording]);

  const handleVoiceTouchStart = useCallback((event: TouchEvent<HTMLButtonElement>) => {
    event.preventDefault();
    if (voicePressingRef.current) {
      return;
    }
    voicePressingRef.current = true;
    void startRecording();
  }, [startRecording]);

  const handleVoiceTouchEnd = useCallback((event: TouchEvent<HTMLButtonElement>) => {
    event.preventDefault();
    voicePressingRef.current = false;
    void stopRecording();
  }, [stopRecording]);

  useEffect(() => () => {
    recognitionRef.current?.stop();
    recognitionRef.current = null;
    if (wechatRecordingRef.current) {
      wechatRecordingRef.current = false;
      void stopWechatVoiceAndTranslate().catch(() => undefined);
    }
  }, []);

  useEffect(() => {
    void preloadWechatVoice().catch((error) => {
      console.warn("wechat voice preload failed", error);
    });
  }, []);

  useEffect(() => {
    if (activeTab === "chat") {
      chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages, activeTab]);

  useEffect(() => {
    if (activeTab !== "chat" || chatHydrated) {
      return;
    }
    const cached = safeReadAssistantChatCache();
    if (cached) {
      setConversationId(cached.conversationId);
      setConversationNo(cached.conversationNo);
      setMessages(normalizeCachedMessages(cached.messages));
    } else {
      setConversationId(undefined);
      setConversationNo(undefined);
      setMessages([DEFAULT_CHAT_MESSAGE]);
    }
    setChatHydrated(true);
  }, [activeTab, chatHydrated]);

  useEffect(() => {
    if (!chatHydrated) {
      return;
    }
    const token = auth.getToken();
    if (!token) {
      storage.remove(ASSISTANT_CHAT_CACHE_KEY);
      return;
    }
    const hasRealChat = Boolean(conversationId || conversationNo || messages.some((item) => item.id !== DEFAULT_CHAT_MESSAGE.id));
    if (!hasRealChat) {
      return;
    }
    storage.set(ASSISTANT_CHAT_CACHE_KEY, {
      token,
      savedAt: Date.now(),
      conversationId,
      conversationNo,
      messages: messages.slice(-MAX_CACHED_CHAT_MESSAGES),
    } satisfies AssistantChatCache);
  }, [chatHydrated, conversationId, conversationNo, messages]);

  const sendMessage = async (text: string) => {
    if (!text.trim() || loading) return;
    const content = text.trim();
    const tempMessageId = -Date.now();
    const userMessageId = tempMessageId;
    const assistantMessageId = tempMessageId - 1;
    let assistantBubbleCreated = false;
    const ensureAssistantBubble = () => {
      if (assistantBubbleCreated) {
        return;
      }
      assistantBubbleCreated = true;
      setMessages((prev) => [
        ...prev,
        { id: assistantMessageId, role: "ai", content: "", time: formatChatTime(), services: [], coaches: [] },
      ]);
    };
    const replaceAssistantBubble = (message: Partial<Message>) => {
      ensureAssistantBubble();
      setMessages((prev) =>
        prev.map((item) =>
          item.id === assistantMessageId
            ? { ...item, ...message }
            : item,
        ),
      );
    };
    const appendAssistantDelta = (delta: string) => {
      if (!delta) {
        return;
      }
      ensureAssistantBubble();
      setMessages((prev) =>
        prev.map((item) =>
          item.id === assistantMessageId
            ? { ...item, content: `${item.content}${delta}` }
            : item,
        ),
      );
    };
    setMessages((prev) => [...prev, { id: userMessageId, role: "user", content, time: formatChatTime() }]);
    setInput("");
    setLoading(true);
    const currentLocation = locationStore.getFresh();
    const locationPayload = currentLocation
      ? {
          longitude: currentLocation.longitude,
          latitude: currentLocation.latitude,
        }
      : {};
    const selectedCity = storage.get<{ name?: string }>(storageKeys.city);
    const cityPayload = selectedCity?.name ? { city_name: selectedCity.name } : {};
    try {
      await assistantApi.chatStream({
        conversation_id: conversationId,
        conversation_no: conversationNo,
        scene: ASSISTANT_SCENE,
        ...cityPayload,
        ...locationPayload,
        content,
      }, {
        onStart: (resp) => {
          setConversationId(resp.conversation_id);
          setConversationNo(resp.conversation_no);
        },
        onDelta: appendAssistantDelta,
        onDone: (resp) => {
          setConversationId(resp.conversation_id);
          setConversationNo(resp.conversation_no);
          replaceAssistantBubble({
            id: Number(resp.assistant_message_id || assistantMessageId),
            content: firstText(resp.reply, "暂时没有生成有效回复，请稍后再试。"),
            services: mapRecommendedServices(resp.recommended_services),
            coaches: mapRecommendedCoaches(resp.recommended_coaches),
          });
        },
        onError: (message) => {
          replaceAssistantBubble({ content: message || "服务咨询暂不可用，请稍后再试。" });
        },
      });
    } catch (error) {
      if (error instanceof RequestError && error.code === 1001) {
        navigate(buildLoginRedirect(`${location.pathname}${location.search}`), { replace: true });
        return;
      }
      if (!assistantBubbleCreated) {
        try {
          const resp = await assistantApi.chat({
            conversation_id: conversationId,
            conversation_no: conversationNo,
            scene: ASSISTANT_SCENE,
            ...cityPayload,
            ...locationPayload,
            content,
          });
          setConversationId(resp.conversation_id);
          setConversationNo(resp.conversation_no);
          replaceAssistantBubble({
            id: Number(resp.assistant_message_id || assistantMessageId),
            content: firstText(resp.reply, "暂时没有生成有效回复，请稍后再试。"),
            services: mapRecommendedServices(resp.recommended_services),
            coaches: mapRecommendedCoaches(resp.recommended_coaches),
          });
        } catch (fallbackError) {
          replaceAssistantBubble(buildAssistantErrorMessage(fallbackError instanceof Error ? fallbackError.message : "服务咨询暂不可用，请稍后再试。"));
        }
      } else {
        replaceAssistantBubble({ content: error instanceof Error ? error.message : "服务咨询暂不可用，请稍后再试。" });
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col h-screen min-h-screen pb-16 overflow-hidden" style={{ background: "#F5F4F1", maxWidth: 480, margin: "0 auto" }}>
      {/* ── Header ── */}
      <div
        className="sticky top-0 z-30 flex-shrink-0 relative overflow-hidden px-5 pt-12 pb-4"
        style={{ background: "linear-gradient(150deg, #FFB07A 0%, #FF8040 55%, #FF6018 100%)" }}
      >
        <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full" style={{ background: "rgba(255,255,255,0.06)" }} />
        <div className="flex items-center justify-between relative z-10 mb-4">
          <div>
            <h1 className="text-base font-bold text-white">妙柔健康</h1>
            <p className="text-[11px] mt-0.5" style={{ color: "rgba(255,255,255,0.7)" }}>健康答疑 · 风险提醒 · 居家护理</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => navigate("/service")}
              className="relative w-9 h-9 rounded-full flex items-center justify-center cursor-pointer flex-shrink-0"
              style={{ background: "rgba(255,255,255,0.15)", border: "1px solid rgba(255,255,255,0.25)" }}
              aria-label="联系客服"
              title="联系客服"
            >
              <i className="ri-customer-service-2-line text-base text-white" />
            </button>
          </div>
        </div>

        {/* Tab switcher */}
        <div className="flex rounded-xl p-0.5 relative z-10" style={{ background: "rgba(0,0,0,0.15)" }}>
          {([
            { key: "home", label: "养生指南", icon: "ri-seedling-line" },
            { key: "chat", label: "健康问答", icon: "ri-chat-smile-2-line" },
            { key: "records", label: "服务记录", icon: "ri-history-line" },
          ] as const).map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className="flex-1 flex items-center justify-center gap-1 py-2 rounded-lg text-xs font-semibold cursor-pointer whitespace-nowrap transition-all"
              style={{
                background: activeTab === tab.key ? "#fff" : "transparent",
                color: activeTab === tab.key ? "#FF6018" : "rgba(255,255,255,0.75)",
              }}
            >
              <i className={`${tab.icon} text-sm`} />
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* ══════════════ HOME TAB ══════════════ */}
      {activeTab === "home" && (
        <div className="flex-1 overflow-y-auto px-5 py-5 space-y-6">
          {/* Next care reminder */}
          <div
            className="relative overflow-hidden rounded-2xl p-5 cursor-pointer"
            style={{ background: "linear-gradient(120deg, #2C1A0E 0%, #5C2D0A 100%)" }}
            onClick={openNextCare}
          >
            <div className="absolute -top-6 -right-6 w-24 h-24 rounded-full" style={{ background: "rgba(255,140,60,0.15)" }} />
            <div className="flex items-center justify-between relative z-10">
              <div>
                <p className="text-[10px] mb-1" style={{ color: "rgba(255,180,100,0.8)" }}>{nextCare.title}</p>
                <p className="text-sm font-bold text-white">{nextCare.mainText}</p>
                <p className="text-[11px] mt-0.5" style={{ color: "rgba(255,200,150,0.7)" }}>{nextCare.subText}</p>
              </div>
              <button
                className="px-3.5 py-2 rounded-xl text-xs font-bold cursor-pointer whitespace-nowrap"
                style={{ background: "#FF6018", color: "#fff" }}
              >
                {nextCare.buttonText}
              </button>
            </div>
          </div>

          {/* Today tip - featured card */}
          <div>
            <div className="flex items-center justify-between mb-3 px-0.5">
              <div className="flex items-center gap-2">
                <div className="w-1 h-4 rounded-full" style={{ background: "linear-gradient(180deg,#FF6018,#FF9A3C)" }} />
                <span className="text-sm font-bold" style={{ color: "#1A1A1A" }}>今日养生</span>
              </div>
              <span className="text-[11px]" style={{ color: "#BBAAA0" }}>{todayTip.headerTag}</span>
            </div>
            <div
              className="rounded-2xl overflow-hidden"
              style={{ background: "#fff", border: "1px solid #F0EAE0" }}
            >
              <div className="relative h-28 overflow-hidden">
                <img
                  src={todayTip.imageUrl}
                  alt={todayTip.title}
                  className="w-full h-full object-cover object-center"
                  onError={(event) => {
                    const image = event.currentTarget;
                    if (image.dataset.fallbackApplied === "1") {
                      image.style.visibility = "hidden";
                      return;
                    }
                    image.dataset.fallbackApplied = "1";
                    image.src = DEFAULT_TODAY_IMAGE;
                  }}
                />
                <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(0,0,0,0) 30%, rgba(0,0,0,0.5) 100%)" }} />
                <span
                  className="absolute top-3 left-3 text-[10px] px-2 py-0.5 rounded-full font-bold"
                  style={{ background: "#17A87B", color: "#fff" }}
                >
                  {todayTip.tag}
                </span>
              </div>
              <div className="px-4 py-4">
                <p className="text-sm font-bold mb-2" style={{ color: "#1A1A1A" }}>{todayTip.title}</p>
                <p className="text-xs leading-relaxed" style={{ color: "#888" }}>
                  {todayTip.body}
                </p>
              </div>
            </div>
          </div>

          {/* Knowledge tip cards - horizontal scroll */}
          <div>
            <div className="flex items-center gap-2 mb-3 px-0.5">
              <div className="w-1 h-4 rounded-full" style={{ background: "linear-gradient(180deg,#FF6018,#FF9A3C)" }} />
              <span className="text-sm font-bold" style={{ color: "#1A1A1A" }}>养生 Tips</span>
            </div>
            <div className="flex gap-3 overflow-x-auto pb-1" style={{ scrollbarWidth: "none" }}>
              {wellnessTips.map((tip) => (
                <div
                  key={tip.id}
                  className="flex-shrink-0 rounded-2xl p-4"
                  style={{ background: "#fff", border: "1px solid #F0EAE0", width: 210 }}
                >
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: tip.iconBg }}>
                      <i className={`${tip.icon} text-sm`} style={{ color: tip.iconColor }} />
                    </div>
                    <span
                      className="text-[10px] px-2 py-0.5 rounded-full font-semibold"
                      style={{ background: tip.tagBg, color: tip.tagColor }}
                    >
                      {tip.tag}
                    </span>
                  </div>
                  <p className="text-xs font-bold mb-1.5 leading-tight" style={{ color: "#1A1A1A" }}>{tip.title}</p>
                  <p className="text-[11px] leading-relaxed line-clamp-3" style={{ color: "#999" }}>{tip.body}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Scene-based service recommendation */}
          <div>
            <div className="flex items-center gap-2 mb-3 px-0.5">
              <div className="w-1 h-4 rounded-full" style={{ background: "linear-gradient(180deg,#FF6018,#FF9A3C)" }} />
              <span className="text-sm font-bold" style={{ color: "#1A1A1A" }}>我是哪类人？</span>
              <span className="text-[11px]" style={{ color: "#BBAAA0" }}>选你的状态</span>
            </div>
            <div className="grid grid-cols-2 gap-3.5">
              {sceneCards.map((scene) => (
                <button
                  key={scene.id}
                  onClick={() => setExpandedScene(expandedScene === scene.id ? null : scene.id)}
                  className="flex items-center gap-3 p-4 py-5 rounded-2xl cursor-pointer transition-all text-left"
                  style={{
                    background: expandedScene === scene.id ? scene.bg : "#fff",
                    border: `1.5px solid ${expandedScene === scene.id ? scene.color : "#F0EAE0"}`,
                  }}
                >
                  <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: scene.bg }}>
                    <i className={`${scene.icon} text-lg`} style={{ color: scene.color }} />
                  </div>
                  <div>
                    <p className="text-xs font-bold" style={{ color: "#1A1A1A" }}>{scene.label}</p>
                    <p className="text-[10px]" style={{ color: "#BBAAA0" }}>{scene.desc}</p>
                  </div>
                </button>
              ))}
            </div>

            {/* expanded services — full width below grid */}
            {expandedScene !== null && (() => {

              const scene = sceneCards.find((s) => s.id === expandedScene);
              if (!scene) {
                return null;
              }
              return (
                <div
                  className="mt-4 rounded-2xl overflow-hidden"
                  style={{ border: `1px solid ${scene.color}40`, background: scene.bg }}
                >
                  <div className="flex items-center gap-2.5 px-5 py-4 border-b" style={{ borderColor: `${scene.color}25` }}>
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: "#fff" }}>
                      <i className={`${scene.icon} text-sm`} style={{ color: scene.color }} />
                    </div>
                    <span className="text-sm font-bold" style={{ color: scene.color }}>{scene.label} · 推荐服务</span>
                  </div>
                  <div className="p-4 space-y-3">
                    {scene.services.map((svc) => (
                      <div
                        key={svc.name}
                        className="flex items-center gap-3.5 p-4 rounded-xl"
                        style={{ background: "#fff" }}
                      >
                        <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: scene.bg }}>
                          <i className="ri-leaf-line text-base" style={{ color: scene.color }} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-bold" style={{ color: "#1A1A1A" }}>{svc.name}</p>
                          <p className="text-xs mt-0.5" style={{ color: "#BBAAA0" }}>{svc.desc}</p>
                          <p className="text-xs mt-1 font-semibold" style={{ color: scene.color }}>{svc.price} · {svc.duration}</p>
                        </div>
                        <button
                          onClick={() => openWellnessService(svc)}
                          className="text-xs px-4 py-2 rounded-full font-bold whitespace-nowrap cursor-pointer text-white flex-shrink-0"
                          style={{ background: scene.color }}
                        >
                          预约
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })()}
          </div>

          {/* Seasonal care tips horizontal */}
          <div>
            <div className="flex items-center gap-2 mb-3 px-0.5">
              <div className="w-1 h-4 rounded-full" style={{ background: "linear-gradient(180deg,#FF6018,#FF9A3C)" }} />
              <span className="text-sm font-bold" style={{ color: "#1A1A1A" }}>时令养生</span>
            </div>
            <div className="grid grid-cols-3 gap-3">
              {seasonalTips.map((t) => (
                <button
                  key={t.title}
                  type="button"
                  onClick={() => setSelectedSeasonalTip(t)}
                  className="flex flex-col items-center py-5 px-2 rounded-2xl text-center cursor-pointer"
                  style={{ background: "#fff", border: "1px solid #F0EAE0" }}
                >
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-2.5" style={{ background: t.bg }}>
                    <i className={`${t.icon} text-base`} style={{ color: t.color }} />
                  </div>
                  <p className="text-xs font-bold leading-tight" style={{ color: "#1A1A1A" }}>{t.title}</p>
                  <p className="text-[11px] mt-1 leading-tight" style={{ color: "#BBAAA0" }}>{t.desc}</p>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ══════════════ CHAT TAB ══════════════ */}
      {activeTab === "chat" && (
        <div className="flex flex-col flex-1 min-h-0">
          {/* Quick ask buttons */}
          <div className="flex-shrink-0 px-5 py-3" style={{ background: "#F5F4F1", borderBottom: "1px solid #EDE8E0" }}>
            <div className="flex flex-wrap gap-1.5">
              {["肩颈酸紧怎么办？", "最近睡不好怎么办？", "久坐腰背不适？", "推拿前注意什么？"].map((q) => (
                <button
                  key={q}
                  onClick={() => sendMessage(q)}
                  className="text-[11px] px-2.5 py-1.5 rounded-full cursor-pointer whitespace-nowrap"
                  style={{ background: "#fff", color: "#FF6018", border: "1px solid #FFD4A0" }}
                >
                  {q}
                </button>
              ))}
            </div>
          </div>

          <div className="flex-1 min-h-0 px-5 pt-4 pb-32 overflow-y-auto space-y-4">
            {messages.map((msg) => (
              <div key={msg.id} className={`flex gap-2 ${msg.role === "user" ? "justify-end" : "justify-start"}`}>
                {msg.role === "ai" && (
                  <div
                    className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 mt-1"
                    style={{ background: "linear-gradient(135deg,#FF8040,#FF6018)" }}
                  >
                    <i className="ri-seedling-line text-sm text-white" />
                  </div>
                )}
                <div className={`flex flex-col gap-1.5 max-w-[78%] ${msg.role === "user" ? "items-end" : "items-start"}`}>
                  <div
                    className="px-3.5 py-3 rounded-2xl text-sm leading-relaxed"
                    style={
                      msg.role === "user"
                        ? { background: "linear-gradient(135deg,#FF8040,#FF6018)", color: "#fff", borderBottomRightRadius: 4 }
                        : { background: "#fff", color: "#1A1A1A", border: "1px solid #F0EAE0", borderBottomLeftRadius: 4 }
                    }
                  >
                    {msg.content.split("\n").map((line, i) => (
                      <p key={i} className={i > 0 ? "mt-1" : ""}>{line}</p>
                    ))}
                  </div>
                  {msg.services && msg.services.length > 0 && (
                    <div className="w-full space-y-1.5">
                      {msg.services.map((svc) => (
                        <div
                          key={svc.name}
                          className="flex items-center gap-2.5 p-3 rounded-xl"
                          style={{ background: "#fff", border: "1px solid #FFD4A0" }}
                        >
                          <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: "#FFF4EC" }}>
                            <i className="ri-leaf-line text-sm" style={{ color: "#FF6018" }} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-xs font-bold" style={{ color: "#1A1A1A" }}>{svc.name}</p>
                            <p className="text-[10px]" style={{ color: "#FF6018" }}>{svc.price} · {svc.duration}</p>
                          </div>
                          <button
                            onClick={() => openWellnessService(svc)}
                            className="text-[10px] px-2.5 py-1.5 rounded-full font-bold whitespace-nowrap cursor-pointer text-white"
                            style={{ background: "linear-gradient(90deg,#FF5C00,#FF9A3C)" }}
                          >
                            查看
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  {msg.coaches && msg.coaches.length > 0 && (
                    <div className="w-full space-y-1.5">
                      {msg.coaches.map((coach) => (
                        <div
                          key={`${coach.coachId || coach.name}`}
                          className="flex items-center gap-2.5 p-3 rounded-xl"
                          style={{ background: "#fff", border: "1px solid #FFD4A0" }}
                        >
                          <div className="w-9 h-9 rounded-full overflow-hidden flex-shrink-0" style={{ background: "#FFF4EC" }}>
                            {coach.photo ? (
                              <img src={coach.photo} alt="" className="h-full w-full object-cover" />
                            ) : (
                              <div className="h-full w-full flex items-center justify-center">
                                <i className="ri-user-heart-line text-sm" style={{ color: "#FF6018" }} />
                              </div>
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-xs font-bold" style={{ color: "#1A1A1A" }}>{coach.name}</p>
                            <p className="text-[10px]" style={{ color: "#BBAAA0" }}>{coach.desc}</p>
                            <p className="text-[10px] mt-0.5" style={{ color: "#FF6018" }}>
                              {coach.rating} · 已服务{coach.orderNum}单
                            </p>
                          </div>
                          <button
                            onClick={() => openCoachDetail(coach)}
                            className="text-[10px] px-2.5 py-1.5 rounded-full font-bold whitespace-nowrap cursor-pointer text-white"
                            style={{ background: "linear-gradient(90deg,#FF5C00,#FF9A3C)" }}
                          >
                            查看
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  <span className="text-[10px] px-0.5" style={{ color: "#C8C0B8" }}>{msg.time}</span>
                </div>
                {msg.role === "user" && (
                  <div className="w-8 h-8 rounded-full overflow-hidden flex-shrink-0 mt-1" style={{ border: "1.5px solid #FFD4A0" }}>
                    <img
                      src="https://readdy.ai/api/search-image?query=young%20asian%20woman%20portrait%20avatar%20minimal%20background&width=80&height=80&seq=chat_avatar_v1&orientation=squarish"
                      alt="我"
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                )}
              </div>
            ))}

            {loading && (
              <div className="flex gap-2">
                <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: "linear-gradient(135deg,#FF8040,#FF6018)" }}>
                  <i className="ri-seedling-line text-sm text-white" />
                </div>
                <div className="px-4 py-3 rounded-2xl flex items-center gap-1.5" style={{ background: "#fff", border: "1px solid #F0EAE0", borderBottomLeftRadius: 4 }}>
                  {[0, 1, 2].map((i) => (
                    <span key={i} className="w-1.5 h-1.5 rounded-full" style={{ background: "#FFB07A", animation: `dot-bounce 1.2s ease-in-out ${i * 0.2}s infinite` }} />
                  ))}
                </div>
              </div>
            )}
            <div ref={chatEndRef} />
          </div>

          {/* Input bar */}
          <div
            className="fixed left-1/2 w-full px-5 py-3"
            style={{ maxWidth: 480, transform: "translateX(-50%)", bottom: 64, background: "#F5F4F1", borderTop: "1px solid #EDE8E0" }}
          >
            {voiceError && (
              <div className="text-center text-xs mb-2 px-3 py-1.5 rounded-full mx-auto w-fit" style={{ background: "#FFF0E8", color: "#FF6018" }}>
                {voiceError}
              </div>
            )}
            {isRecording && (
              <div className="flex items-center justify-center gap-2 mb-2">
                <span className="flex gap-1">
                  {[0,1,2,3].map((i) => (
                    <span key={i} className="w-1 rounded-full" style={{ height: 16, background: "#FF6018", animation: `voice-bar 0.8s ease-in-out ${i * 0.15}s infinite alternate` }} />
                  ))}
                </span>
                <span className="text-xs font-medium" style={{ color: "#FF6018" }}>正在聆听…</span>
              </div>
            )}
            <div
              className="flex items-center gap-2 px-3 py-2.5 rounded-2xl"
              style={{
                background: "#fff",
                border: `1.5px solid ${isRecording ? "#FF6018" : "#EDE8E0"}`,
                transition: "border-color 0.2s",
              }}
            >
              {/* mic button */}
              <button
                type="button"
                onPointerDown={handleVoicePointerDown}
                onPointerUp={handleVoicePointerUp}
                onPointerCancel={handleVoicePointerUp}
                onTouchStart={handleVoiceTouchStart}
                onTouchEnd={handleVoiceTouchEnd}
                onTouchCancel={handleVoiceTouchEnd}
                onContextMenu={(e) => e.preventDefault()}
                aria-label="按住说话，松手转文字"
                title="按住说话，松手转文字"
                className="w-9 h-9 rounded-full flex items-center justify-center cursor-pointer flex-shrink-0 transition-all select-none touch-none"
                style={{
                  background: isRecording ? "linear-gradient(135deg,#FF8040,#FF6018)" : "#F5F4F1",
                  border: isRecording ? "none" : "1px solid #EDE8E0",
                }}
              >
                <Mic size={17} strokeWidth={2.3} color={isRecording ? "#fff" : "#BBAAA0"} />
              </button>
              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    sendMessage(input);
                  }
                }}
                placeholder={isRecording ? "松手后会转成文字…" : "描述不适部位、持续时间…"}
                className="flex-1 min-w-0 text-sm outline-none bg-transparent"
                style={{ color: "#1A1A1A" }}
              />
              <button
                type="button"
                onClick={() => sendMessage(input)}
                disabled={!input.trim() || loading}
                aria-label="发送"
                title="发送"
                className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 transition-all"
                style={{
                  background: input.trim() ? "linear-gradient(135deg,#FF8040,#FF6018)" : "#F0EAE0",
                  cursor: input.trim() && !loading ? "pointer" : "not-allowed",
                  minWidth: 36,
                }}
              >
                <Send size={16} strokeWidth={2.4} color={input.trim() ? "#fff" : "#C8C0B8"} />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ══════════════ RECORDS TAB ══════════════ */}
      {activeTab === "records" && (
        <div className="px-5 py-4 space-y-3">
          {/* Stats row */}
          <div className="grid grid-cols-3 gap-2">
            {serviceRecordStats.map((s) => (
              <div key={s.label} className="flex flex-col items-center py-3.5 rounded-2xl" style={{ background: "#fff", border: "1px solid #F0EAE0" }}>
                <div className="w-8 h-8 rounded-xl flex items-center justify-center mb-1.5" style={{ background: s.bg }}>
                  <i className={`${s.icon} text-sm`} style={{ color: s.color }} />
                </div>
                <p className="text-sm font-bold" style={{ color: "#1A1A1A" }}>{s.value}</p>
                <p className="text-[10px] mt-0.5" style={{ color: "#BBAAA0" }}>{s.label}</p>
              </div>
            ))}
          </div>

          {/* Service history */}
          <div className="flex items-center justify-between px-0.5">
            <span className="text-sm font-bold" style={{ color: "#1A1A1A" }}>服务历史</span>
            <button onClick={() => navigate("/orders")} className="text-xs cursor-pointer whitespace-nowrap" style={{ color: "#FF6018" }}>
              查看订单 &gt;
            </button>
          </div>

          {recordsLoading && (
            <div className="p-4 rounded-2xl text-center text-xs" style={{ background: "#fff", border: "1px solid #F0EAE0", color: "#BBAAA0" }}>
              正在加载服务记录…
            </div>
          )}

          {!recordsLoading && recordsError && (
            <div className="p-4 rounded-2xl text-center text-xs" style={{ background: "#fff", border: "1px solid #F0EAE0", color: "#FF6018" }}>
              {recordsError}
            </div>
          )}

          {!recordsLoading && !recordsError && serviceRecords.length === 0 && (
            <div className="p-4 rounded-2xl text-center text-xs" style={{ background: "#fff", border: "1px solid #F0EAE0", color: "#BBAAA0" }}>
              暂无已完成的服务记录
            </div>
          )}

          {!recordsLoading && !recordsError && serviceRecords.slice(0, 5).map((r) => (
            <div key={r.id} className="flex items-start gap-3 p-4 rounded-2xl" style={{ background: "#fff", border: "1px solid #F0EAE0" }}>
              <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: "#FFF4EC" }}>
                <i className="ri-leaf-line text-base" style={{ color: "#FF6018" }} />
              </div>
              <div className="flex-1">
                <div className="flex items-center justify-between mb-0.5">
                  <span className="text-sm font-bold" style={{ color: "#1A1A1A" }}>{r.service}</span>
                  <span className="text-[11px]" style={{ color: "#BBAAA0" }}>{r.date}</span>
                </div>
                <p className="text-[11px]" style={{ color: "#999" }}>技师：{r.therapist}</p>
                <div className="flex items-center gap-0.5 my-1">
                  {Array.from({ length: 5 }).map((_, j) => (
                    <i key={j} className={r.reviewed && j < r.score ? "ri-star-fill text-xs" : "ri-star-line text-xs"} style={{ color: "#FF7B22" }} />
                  ))}
                </div>
                <p className="text-[11px] px-2.5 py-1 rounded-lg inline-block" style={{ background: "#FFF4EC", color: "#FF6018" }}>
                  "{r.feedback}"
                </p>
              </div>
            </div>
          ))}

          {/* next booking hint */}
          <div
            className="flex items-center gap-3 p-4 rounded-2xl cursor-pointer"
            style={{ background: "linear-gradient(120deg,#FF8040,#FF5C00)" }}
            onClick={() => navigate("/booking")}
          >
            <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: "rgba(255,255,255,0.2)" }}>
              <i className="ri-calendar-2-line text-lg text-white" />
            </div>
            <div className="flex-1">
              <p className="text-sm font-bold text-white">预约下一次调理</p>
              <p className="text-[11px]" style={{ color: "rgba(255,255,255,0.75)" }}>建议14天调理一次，效果最佳</p>
            </div>
            <i className="ri-arrow-right-s-line text-xl text-white opacity-70" />
          </div>
        </div>
      )}

      <BottomNav />

      {selectedSeasonalTip && (
        <div className="fixed inset-0 z-[90] flex items-center justify-center px-6" style={{ background: "rgba(36,26,18,0.42)" }}>
          <button
            type="button"
            aria-label="关闭时令养生详情"
            className="absolute inset-0 cursor-default"
            onClick={() => setSelectedSeasonalTip(null)}
          />
          <div
            className="relative max-h-[82vh] w-full overflow-y-auto rounded-[28px] p-5"
            style={{ maxWidth: 420, background: "#FFFCF8", boxShadow: "0 20px 48px rgba(44,26,14,0.22)" }}
          >
            <div className="absolute left-0 top-0 h-1.5 w-full" style={{ background: selectedSeasonalTip.color }} />
            <button
              type="button"
              onClick={() => setSelectedSeasonalTip(null)}
              className="absolute right-4 top-4 flex h-9 w-9 items-center justify-center rounded-full cursor-pointer"
              style={{ background: "#F7F1EA", color: "#BBAAA0" }}
              aria-label="关闭"
            >
              <i className="ri-close-line text-xl" />
            </button>
            <div className="pt-4">
              <div className="mb-5 flex items-start gap-3.5 pr-10">
                <div
                  className="flex h-14 w-14 flex-shrink-0 items-center justify-center rounded-2xl"
                  style={{ background: selectedSeasonalTip.bg }}
                >
                  <i className={`${selectedSeasonalTip.icon} text-2xl`} style={{ color: selectedSeasonalTip.color }} />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-bold" style={{ color: selectedSeasonalTip.color }}>时令养生</p>
                  <h3 className="mt-1 text-xl font-bold leading-snug" style={{ color: "#1A1A1A" }}>
                    {selectedSeasonalTip.detailTitle}
                  </h3>
                </div>
              </div>

              <div className="space-y-4">
                <div className="border-l-4 pl-3.5" style={{ borderColor: selectedSeasonalTip.color }}>
                  <p className="text-sm font-bold" style={{ color: "#1A1A1A" }}>今天可以这样做</p>
                  {selectedSeasonalParagraphs.map((paragraph, index) => (
                    <p key={index} className="mt-2 text-sm leading-7" style={{ color: "#6F6258" }}>
                      {paragraph}
                    </p>
                  ))}
                </div>

                <div className="flex items-center justify-between gap-3 border-t pt-3.5" style={{ borderColor: "#F0EAE0" }}>
                  <div className="min-w-0">
                    <p className="text-[11px] font-semibold" style={{ color: "#A89483" }}>内容来源</p>
                    {selectedSeasonalTip.sourceUrl ? (
                      <a
                        href={selectedSeasonalTip.sourceUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="mt-0.5 block truncate text-xs font-bold"
                        style={{ color: "#6F6258" }}
                      >
                        {selectedSeasonalTip.sourceName || selectedSeasonalTip.sourceUrl}
                      </a>
                    ) : (
                      <p className="mt-0.5 truncate text-xs font-bold" style={{ color: "#6F6258" }}>
                        {selectedSeasonalTip.sourceName || "运营配置"}
                      </p>
                    )}
                  </div>
                  <span className="flex-shrink-0 rounded-full px-3 py-1.5 text-[11px] font-bold" style={{ background: selectedSeasonalTip.bg, color: selectedSeasonalTip.color }}>
                    仅作日常参考
                  </span>
                </div>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setSelectedSeasonalTip(null)}
              className="mt-5 w-full rounded-2xl py-3.5 text-sm font-bold cursor-pointer"
              style={{ background: selectedSeasonalTip.color, color: "#fff" }}
            >
              知道了
            </button>
          </div>
        </div>
      )}

      <style>{`
        .line-clamp-3 { display: -webkit-box; -webkit-line-clamp: 3; -webkit-box-orient: vertical; overflow: hidden; }
        @keyframes dot-bounce {
          0%, 80%, 100% { transform: translateY(0); opacity: 0.4; }
          40% { transform: translateY(-5px); opacity: 1; }
        }
        @keyframes voice-bar {
          0% { height: 4px; opacity: 0.5; }
          100% { height: 16px; opacity: 1; }
        }

      `}</style>
    </div>
  );
}
